// Inteligencia › Buscador de obra (ex «Maquinaria»): buscador de objetos en el archivo de fotos de la cámara, como el
// «Select an object» de Evercam. La portada es un buscador + pastillas por categoría (pictograma + nombre, sin fotos
// ni números); al elegir una aparecen todas las fotos donde el modelo la encontró, con el recuadro, fecha y hora.
// Clic = foto grande con zoom (el visor del Calendario).
import React, { useEffect, useMemo, useState } from 'react';
import { T, Btn, Card, inputStyle } from '../ui';
import { fmtHM, fmtDM, fmtKey, fmtDow, dayKey, listDays } from '../iaData';
import { Photo, Visor } from '../CalendarioObra';
import { iou } from '../entregas';
import { CATEGORIAS, catInfo, cargarPersonas, recorte, plural, claveCorreccion, leerCorrecciones, guardarCorreccion } from './datosV3';
import { useFotosV3, useNarrow, Encabezado, Rango, Nota, Cargando, Vacio, SinAnalisis, textoRango } from './comun';
import { Pictograma } from './pictogramas';

const R169 = 16 / 9;
const norm = (s) => String(s || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
// Palabras con que la gente busca cada cosa
const SINONIMOS = {
  volquete: 'volcador batea tolva', camion: 'camion playo semi acoplado flete', mixer: 'hormigonera motohormigonera mixer hormigon',
  bomba: 'bomba pluma hormigon', tanque: 'tanque cisterna acoplado agua combustible', camioneta: 'pickup pick up utilitario trafic', auto: 'auto coche vehiculo particular',
  excavadora: 'excavadora pala oruga', retro: 'retro pala retroexcavadora', minicargadora: 'bobcat minicargadora skid',
  grua_movil: 'grua movil pluma', autoelevador: 'clark montacargas autoelevador', grua_torre: 'grua torre',
  persona: 'persona obrero trabajador gente casco', contenedor: 'contenedor obrador container trailer volquete tacho',
  pallet: 'pallet ladrillo bloque ceramico', bolson: 'bolson cemento cal bolsa big bag', arena: 'arena tierra pila',
  piedra: 'piedra grava ripio canto', hierros: 'hierro malla varilla acero', madera: 'madera tabla fenolico encofrado',
  perfiles: 'cano perfil tubo metal',
};

// Mejor detección de cada foto para una categoría
function resultados(fotos, cat) {
  const clases = new Set(cat.yolox || cat.cls);
  const out = [];
  for (const p of fotos) {
    const ds = p.dets.filter((d) => clases.has(d.cls) && d.x1 != null);
    if (!ds.length) continue;
    const mejor = ds.reduce((a, d) => (d.conf * Math.sqrt(d.x2 - d.x1) > a.conf * Math.sqrt(a.x2 - a.x1) ? d : a));
    out.push({ p, ds, mejor, n: ds.length });
  }
  return out;
}

// Grupos livianos de la portada (Obra va con Máquinas)
const GRUPO_PORTADA = { 'Vehículos': 'Vehículos', 'Máquinas': 'Máquinas', Obra: 'Máquinas', Personas: 'Personas', Materiales: 'Materiales' };

// Pastilla de la portada: pictograma + nombre. Con detecciones en el rango: a todo color y en negrita (con un punto);
// sin detecciones: apagada pero se puede tocar (adentro dice «No encontramos…»).
function Pastilla({ cat, hay, onElegir }) {
  const apagada = cat.proximamente || hay === false;
  return (
    <button type="button" onClick={() => !cat.proximamente && onElegir(cat.key)} disabled={cat.proximamente}
      title={cat.proximamente ? 'Todavía no está en el reconocimiento' : hay === false ? `No encontramos ${cat.label.toLowerCase()} en estas fechas` : cat.label}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 7, padding: '6px 12px 6px 8px', borderRadius: 999, background: '#fff',
        border: `1px solid ${apagada ? T.line : cat.color + '66'}`, cursor: cat.proximamente ? 'not-allowed' : 'pointer', opacity: apagada ? 0.5 : 1,
        fontSize: 13.5, color: apagada ? T.muted : T.text, fontWeight: apagada ? 400 : 650, lineHeight: 1.2, boxShadow: apagada ? 'none' : '0 1px 2px rgba(0,0,0,.05)' }}>
      <Pictograma k={cat.key} color={apagada ? '#9ca3af' : cat.color} />
      {cat.label}
      {hay && <span aria-hidden style={{ width: 6, height: 6, borderRadius: 999, background: cat.color }} />}
      {cat.proximamente && <span style={{ fontSize: 11, color: T.muted }}>· pronto</span>}
    </button>
  );
}

function Resultados({ cat, res, rango, onVolver, narrow, corregidas = 0, onCorregir }) {
  const [orden, setOrden] = useState('reciente');
  const [dia, setDia] = useState(null);
  const [cuantos, setCuantos] = useState(36);
  const [visor, setVisor] = useState(null);
  const [agrupar, setAgrupar] = useState(true);
  useEffect(() => { setCuantos(36); }, [orden, dia, agrupar]);
  const porDia = useMemo(() => {
    const m = new Map();
    res.forEach((r) => { const k = dayKey(r.p.ms); m.set(k, (m.get(k) || 0) + 1); });
    return m;
  }, [res]);
  const dias = listDays(rango.desde, rango.hasta);
  const maxDia = Math.max(1, ...porDia.values());
  const lista = useMemo(() => {
    const xs = res.filter((r) => !dia || dayKey(r.p.ms) === dia);
    return orden === 'reciente' ? [...xs].sort((a, b) => b.p.ms - a.p.ms) : [...xs].sort((a, b) => b.mejor.conf - a.mejor.conf);
  }, [res, orden, dia]);
  // Fotos seguidas del mismo objeto en el mismo lugar (un camión estacionado 2 horas) = una sola tarjeta
  const tarjetas = useMemo(() => {
    if (!agrupar) return lista.map((r, i) => ({ r, i, n: 1, desde: r.p.ms, hasta: r.p.ms, todos: [r] }));
    const porTiempo = lista.map((r, i) => ({ r, i })).sort((a, b) => a.r.p.ms - b.r.p.ms);
    const grupos = [];
    for (const x of porTiempo) {
      const g = grupos[grupos.length - 1];
      if (g && dayKey(g.ult.p.ms) === dayKey(x.r.p.ms) && x.r.p.ms - g.ult.p.ms <= 25 * 60e3 && iou(g.ult.mejor, x.r.mejor) > 0.3) {
        g.items.push(x); g.ult = x.r;
      } else grupos.push({ items: [x], ult: x.r });
    }
    const out = grupos.map((g) => {
      const top = g.items.reduce((a, x) => (x.r.mejor.conf > a.r.mejor.conf ? x : a));
      return { r: top.r, i: Math.min(...g.items.map((x) => x.i)), n: g.items.length, desde: g.items[0].r.p.ms, hasta: g.items[g.items.length - 1].r.p.ms, todos: g.items.map((x) => x.r) };
    });
    return orden === 'reciente' ? out.sort((a, b) => b.hasta - a.hasta) : out.sort((a, b) => b.r.mejor.conf - a.r.mejor.conf);
  }, [lista, agrupar, orden]);
  const fotosVisor = useMemo(() => lista.map((r) => ({
    photo: r.p, label: `${fmtDow(dayKey(r.p.ms))} ${fmtDM(r.p.ms)} ${fmtHM(r.p.ms)}`,
    boxes: r.ds.map((d) => ({ ...d, color: cat.color })), zoom: recorte(r.p, r.mejor, { k: 3, ratio: R169, min: 0.15 }),
  })), [lista, cat]);
  return (
    <Card
      title={<><Btn onClick={onVolver}>‹ Volver</Btn><Pictograma k={cat.key} color={cat.color} size={24} />{cat.label}<span style={{ color: T.muted, fontWeight: 400, fontSize: 13 }}>· {plural(lista.length, 'foto', 'fotos')}</span></>}
      right={(
        <>
        <label style={{ fontSize: 12.5, color: T.muted, display: 'inline-flex', alignItems: 'center', gap: 5, cursor: 'pointer' }}>
          <input type="checkbox" checked={agrupar} onChange={(e) => setAgrupar(e.target.checked)} />Juntar fotos seguidas
        </label>
        <select value={orden} onChange={(e) => setOrden(e.target.value)} style={inputStyle} aria-label="Orden">
          <option value="reciente">Más recientes primero</option>
          <option value="claras">Más claras primero</option>
        </select>
        </>
      )}
    >
      {/* Fotos por día: tocá un día para ver solo ese */}
      <div style={{ display: 'flex', gap: 4, alignItems: 'flex-end', overflowX: 'auto', paddingBottom: 4, marginBottom: 12 }}>
        {dias.map((k) => {
          const n = porDia.get(k) || 0;
          const on = dia === k;
          return (
            <button key={k} type="button" disabled={!n} onClick={() => setDia(on ? null : k)} title={`${fmtKey(k)}: ${plural(n, 'foto', 'fotos')}`}
              style={{ flex: '1 0 34px', minWidth: 34, background: 'none', border: 'none', padding: 0, cursor: n ? 'pointer' : 'default' }}>
              <div style={{ height: 46, display: 'flex', alignItems: 'flex-end' }}>
                <div style={{ width: '100%', height: n ? `${Math.max(10, (100 * n) / maxDia)}%` : 3, borderRadius: 4, background: n ? (on || !dia ? cat.color : cat.color + '55') : T.line }} />
              </div>
              <div style={{ fontSize: 10, color: on ? T.blue : T.muted, fontWeight: on ? 700 : 400, marginTop: 2 }}>{fmtDow(k)} {k.slice(8)}</div>
            </button>
          );
        })}
      </div>
      {dia && <div style={{ fontSize: 12, color: T.muted, marginBottom: 8 }}>Mostrando solo el {fmtKey(dia)} · <button type="button" onClick={() => setDia(null)} style={{ color: T.blue, background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>ver todas las fechas</button></div>}
      {!lista.length ? (
        <Vacio>No encontramos {cat.label.toLowerCase()} en las fotos del {textoRango(rango)}.</Vacio>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: `repeat(${narrow ? 2 : 4}, minmax(0, 1fr))`, gap: 10 }}>
          {tarjetas.slice(0, cuantos).map(({ r, i, n, desde, hasta, todos }) => (
            <div key={r.p.id} style={{ minWidth: 0 }}>
              <Photo photo={r.p} crop={recorte(r.p, r.mejor, { k: 2.4, ratio: R169, min: 0.1 })} boxes={r.ds.map((d) => ({ ...d, color: cat.color }))}
                onClick={() => setVisor(i)} style={{ cursor: 'zoom-in', borderRadius: 10 }}>
                {n > 1 && <span style={{ position: 'absolute', right: 6, bottom: 6, fontSize: 11, fontWeight: 700, background: 'rgba(17,24,39,.75)', color: '#fff', borderRadius: 999, padding: '1px 7px' }}>{n} fotos</span>}
              </Photo>
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: 4, fontSize: 12, marginTop: 4 }}>
                <span style={{ color: T.text, fontWeight: 600 }}>{fmtDow(dayKey(desde))} {fmtDM(desde)} · {fmtHM(desde)}{n > 1 ? ` a ${fmtHM(hasta)}` : ''}</span>
                {r.n > 1 && <span style={{ color: T.muted }}>{r.n} en la foto</span>}
              </div>
              {onCorregir && (
                <button type="button" onClick={() => onCorregir(todos)} title={`No es ${cat.label.toLowerCase()}: se saca de la lista y queda guardado para mejorar el reconocimiento`}
                  style={{ marginTop: 3, fontSize: 11.5, color: T.muted, background: 'none', border: `1px solid ${T.line}`, borderRadius: 999, padding: '1px 8px', cursor: 'pointer' }}>
                  ✕ No es esto
                </button>
              )}
            </div>
          ))}
        </div>
      )}
      {corregidas > 0 && <div style={{ fontSize: 12, color: T.muted, marginTop: 10 }}>{plural(corregidas, 'foto marcada', 'fotos marcadas')} como «No es esto»: no se muestran y quedan guardadas para reentrenar el reconocimiento.</div>}
      {tarjetas.length > cuantos && <div style={{ textAlign: 'center', marginTop: 14 }}><Btn onClick={() => setCuantos((c) => c + 36)}>Ver más ({tarjetas.length - cuantos})</Btn></div>}
      {visor != null && <Visor fotos={fotosVisor} inicio={visor} zoomZona={fotosVisor[visor]?.zoom} onClose={() => setVisor(null)} />}
    </Card>
  );
}

export default function Maquinaria({ feedId, customerId, cam, name }) {
  const narrow = useNarrow();
  const { rango, setRango, cargando, fotos, fuente } = useFotosV3(feedId, 7);
  const [q, setQ] = useState('');
  const [sel, setSel] = useState(null);
  const [personas, setPersonas] = useState(null); // se cargan solo si se buscan
  useEffect(() => { setPersonas(null); }, [rango.desde, rango.hasta]);
  useEffect(() => {
    let vivo = true;
    if (sel === 'persona' && personas == null) cargarPersonas(cam, customerId, rango.desde, rango.hasta).then((xs) => vivo && setPersonas(xs));
    return () => { vivo = false; };
  }, [sel, personas, cam, customerId, rango.desde, rango.hasta]);

  // "No es esto": correcciones del cliente (se ocultan y se guardan para reentrenar)
  const [correc, setCorrec] = useState(() => leerCorrecciones(feedId));
  useEffect(() => { setCorrec(leerCorrecciones(feedId)); }, [feedId]);
  const corregir = async (rs) => {
    let todas = correc;
    for (const r of rs) todas = await guardarCorreccion(feedId, r.p, r.mejor);
    setCorrec({ ...todas });
  };
  const sinCorregir = (xs) => xs.filter((r) => !correc[claveCorreccion(r.p, r.mejor)]);
  const porCat = useMemo(() => {
    const m = {};
    for (const c of CATEGORIAS) if (!c.yolox && !c.proximamente) m[c.key] = resultados(fotos, c);
    if (personas) m.persona = resultados(personas, catInfo('persona'));
    return m;
  }, [fotos, personas]);

  const visibles = CATEGORIAS.filter((c) => {
    const t = norm(q).trim();
    if (!t) return true;
    return t.split(/\s+/).every((w) => norm(`${c.label} ${c.grupo} ${SINONIMOS[c.key] || ''}`).includes(w));
  });
  const gruposPortada = ['Vehículos', 'Máquinas', 'Personas', 'Materiales'].filter((g) => visibles.some((c) => GRUPO_PORTADA[c.grupo] === g));
  const cat = sel ? catInfo(sel) : null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <Encabezado icono="🔎" titulo="Buscador de obra" obra={name} bajada="Buscá vehículos, máquinas, personas o materiales en las fotos de la cámara">
        <Rango rango={rango} setRango={setRango} />
      </Encabezado>
      {cargando ? <Card><Cargando /></Card> : fuente !== 'iaV3' && !sel ? <Card><SinAnalisis /></Card> : cat ? (
        cat.yolox && personas == null ? <Card><Cargando texto="Buscando personas en las fotos…" /></Card>
          : <Resultados cat={cat} res={sinCorregir(porCat[cat.key] || [])} corregidas={(porCat[cat.key] || []).length - sinCorregir(porCat[cat.key] || []).length}
            onCorregir={cat.yolox ? null : corregir} rango={rango} narrow={narrow} onVolver={() => setSel(null)} />
      ) : (
        <Card style={{ overflow: 'visible' }} bodyStyle={{ overflow: 'visible' }}>
          {/* Buscador fijo arriba (también en el celular) */}
          <div style={{ position: 'sticky', top: 0, zIndex: 3, background: '#fff', paddingBottom: 10, marginBottom: 4 }}>
            <div style={{ fontSize: 15, fontWeight: 700, color: T.text, marginBottom: 8 }}>¿Qué querés buscar en las fotos?</div>
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscá: mixer, ladrillos, retro, cemento, persona…" aria-label="Buscar en las fotos"
              style={{ ...inputStyle, width: '100%', boxSizing: 'border-box', fontSize: 15, padding: '10px 14px', borderRadius: 12 }} />
          </div>
          {!visibles.length && <Vacio>No tenemos una categoría para “{q}”. Probá con otra palabra (camión, mixer, pallet, persona…).</Vacio>}
          {gruposPortada.map((g) => (
            <div key={g} style={{ marginTop: 10 }}>
              <div style={{ fontSize: 11.5, fontWeight: 700, color: T.muted, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 6 }}>{g}</div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                {visibles.filter((c) => GRUPO_PORTADA[c.grupo] === g).map((c) => (
                  <Pastilla key={c.key} cat={c} hay={c.yolox ? (personas ? !!sinCorregir(porCat.persona || []).length : null) : !!sinCorregir(porCat[c.key] || []).length} onElegir={setSel} />
                ))}
              </div>
            </div>
          ))}
          <div style={{ marginTop: 14 }}>
            <Nota>
              Se busca en las fotos que la cámara manda a la plataforma. En negrita, lo que apareció en las fechas elegidas; apagado, lo que no.
              Un objeto que pasó entre dos fotos no aparece, y lo que está lejos o tapado puede no reconocerse. La grúa torre todavía no está en el reconocimiento.
            </Nota>
          </div>
        </Card>
      )}
    </div>
  );
}
